import type { CSSProperties, FC } from "react"

interface IconProps {
  reactSvg?: JSX.Element | null
  patternTransform?: boolean
  strokeColor?: string
  strokeWidth?: number
  width: number
  src?: string
  id?: string
}

export const MaskIcon: FC<IconProps> = ({
  patternTransform = true,
  strokeColor = "#232734",
  strokeWidth = 1,
  reactSvg,
  width = 44,
  src,
  id = "",
}) => (
  <svg fill="none" height={width} viewBox="0 0 89 89" width={width}>
    <defs>
      {reactSvg ? (
        <pattern
          height="100"
          id={id}
          patternTransform={patternTransform ? `translate(${width / 2.45}, ${width / 2.45})` : ""}
          patternUnits="userSpaceOnUse"
          width="100"
        >
          {reactSvg}
        </pattern>
      ) : (
        <pattern height="100%" id={id} patternUnits="userSpaceOnUse" width="100%">
          <image height="100%" href={src} width="100%" />
        </pattern>
      )}
    </defs>
    <path
      d="M66.63 79.99c-6.43 3.778-14.717 7.51-22.639 7.51-12.037 0-22.402-3.404-29.995-10.507C6.405 69.89 1.525 59.033.563 44.01c-.634-10.283 3.571-19.763 9.642-27.275 1.766-2.173 4.337-4.203 7.131-6.045 2.79-1.84 5.776-3.472 8.355-4.861l.006-.004C31.862 2.396 36.532.5 44.072.5c14.96 0 26.583 7.556 34.405 19.147C83.219 26.785 87.5 35.1 87.5 44.042c0 16.063-7.874 28.367-20.87 35.947Z"
      fill={`url(#${id})`}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
    />
  </svg>
)

// SVG path for the mask shape (scaled to 42x42 viewBox)
const MASK_PATH =
  "M41 21.0191C41 28.4067 37.373 34.0718 31.4012 37.555C28.4702 39.2775 24.66 41 20.9963 41C9.96862 41 1.90852 34.7607 1.02923 21.0191C0.73614 16.2727 2.67788 11.9091 5.46228 8.46411C7.11094 6.43541 10.2251 4.71288 12.5698 3.44972C15.3909 1.88035 17.5524 1 21.0329 1C27.9207 1 33.2697 4.48325 36.8601 9.80383C39.0216 13.0574 41 16.8851 41 21.0191Z"

interface MaskIconV2Props {
  src?: string
  width?: number
  selected?: boolean
  strokeColor?: string
  strokeWidth?: number
  additionalStyle?: CSSProperties
  alt?: string
}

export const MaskIconV2: FC<MaskIconV2Props> = ({
  src,
  width = 64,
  selected = false,
  strokeColor = "#1D2028",
  strokeWidth = 1,
  additionalStyle,
  alt: _alt = "Masked icon",
}) => {
  const adjustedWidth = width - (selected ? 2 : 0.5)
  const finalStrokeWidth = strokeWidth ?? (selected ? 2 : 0.5)
  const maskId = `mask-${Math.random().toString(36).substr(2, 9)}`

  return (
    <div
      style={{
        height: `${width}px`,
        position: "relative",
        width: `${width}px`,
        ...additionalStyle,
      }}
    >
      <svg
        height={adjustedWidth}
        style={{
          left: 0,
          position: "absolute",
          top: 0,
        }}
        viewBox="0 0 42 42"
        width={adjustedWidth}
      >
        <defs>
          <mask id={maskId}>
            <path d={MASK_PATH} fill="white" />
          </mask>
          <clipPath id={`${maskId}-clip`}>
            <path d={MASK_PATH} />
          </clipPath>
        </defs>

        {/* Image with mask applied */}
        {src && (
          <image
            clipPath={`url(#${maskId}-clip)`}
            height="42"
            href={src}
            preserveAspectRatio="xMidYMid slice"
            width="42"
            x="0"
            y="0"
          />
        )}

        {/* Border stroke */}
        {src && (
          <path d={MASK_PATH} fill="none" stroke={strokeColor} strokeWidth={finalStrokeWidth} />
        )}
      </svg>
    </div>
  )
}
